import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneRight } from "@phosphor-icons/react";
import { API } from "@/lib/api";

function sessionId() {
  let s = localStorage.getItem("dommar_chat_session");
  if (!s) { s = "sess-" + Math.random().toString(36).slice(2); localStorage.setItem("dommar_chat_session", s); }
  return s;
}

export default function Chatbot({ content }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const welcome = content?.chatbot?.welcome || "Hola, ¿en qué puedo ayudarte?";

  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ role: "assistant", text: welcome }]);
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Lo siento, ha ocurrido un error." };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      <motion.button
        data-testid="chatbot-toggle"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-ink text-stone-50 flex items-center justify-center shadow-lg shadow-black/20"
      >
        {open ? <X size={24} /> : <ChatCircleDots size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[90vw] max-w-[380px] h-[520px] bg-white rounded-2xl border border-black/10 shadow-2xl shadow-black/10 flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-black/5 bg-stone-50">
              <p className="font-serif text-lg text-ink leading-none">Asistente DOMMAR STUDIO</p>
              <p className="font-sans text-xs text-faint mt-1">Impulsado por IA · en línea</p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl font-sans text-sm leading-relaxed ${
                      m.role === "user" ? "bg-ink text-stone-50 rounded-br-sm" : "bg-stone-100 text-ink rounded-bl-sm"
                    }`}
                  >
                    {m.text || <span className="opacity-40">…</span>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-black/5 flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Escribe tu mensaje…"
                className="flex-1 bg-stone-100 rounded-full px-4 py-2.5 font-sans text-sm outline-none focus:ring-1 focus:ring-ink/20"
              />
              <button data-testid="chat-send" onClick={send} disabled={busy} className="w-10 h-10 rounded-full bg-ink text-stone-50 flex items-center justify-center disabled:opacity-40 shrink-0">
                <PaperPlaneRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
